import { getExtensionsManager } from '../../extensions/extensions-manager'
import { supportsGlobalSearch } from '../../extensions/utils'

import type {
  GlobalSearchActionUI,
  GlobalSearchCategoryUI,
  GlobalSearchManager,
  GlobalSearchResultPreview,
} from './global-search-models'
import type {
  GlobalSearchAction,
  GlobalSearchCategoryColumn,
  GlobalSearchContext,
  GlobalSearchResult,
  GlobalSearchShellExtension,
  GlobalSearchValueField,
} from '../../extensions'
import { isCommandHandled } from '../../services/namespaces'

class GlobalSearchManagerImpl implements GlobalSearchManager {
  private categoriesMapInstance: Map<string, GlobalSearchCategoryUI> | undefined = undefined
  private globalSearchExtensions: readonly GlobalSearchShellExtension[] | undefined = undefined

  private getGlobalSearchExtensions = (): readonly GlobalSearchShellExtension[] => {
    if (!this.globalSearchExtensions) {
      const result: GlobalSearchShellExtension[] = []

      getExtensionsManager()
        .getExtensions()
        .forEach(extension => {
          if (supportsGlobalSearch(extension)) {
            result.push(extension)
          }
        })
      this.globalSearchExtensions = result
    }
    return this.globalSearchExtensions
  }

  public getCategories = () => Array.from(this.getCategoriesMap().values())

  public getCategoryById = (id: string) => this.getCategoriesMap().get(id)

  private getCategoryId = (result: GlobalSearchResult) => {
    const extensions = this.getGlobalSearchExtensions()
    for (const extension of extensions) {
      const categoryId = extension.getGlobalSearchResultCategoryId(result)
      if (categoryId) {
        return categoryId
      }
    }
  }

  public getCategory = (result: GlobalSearchResult) => {
    const categoryId = this.getCategoryId(result)
    if (categoryId) {
      return this.getCategoryById(categoryId)
    }
  }

  public getGlobalSearchResultActions = (searchResult: GlobalSearchResult, context: GlobalSearchContext) => {
    const searchResultActionsUI: GlobalSearchActionUI[] = []
    this.getGlobalSearchExtensions().forEach(extension => {
      extension.getGlobalSearchResultActions(searchResult, context)?.forEach(action => {
        if (this.canAddActionTo(action, searchResultActionsUI)) {
          searchResultActionsUI.push(this.convertActionToUI(action, extension))
        }
      })
    })
    return searchResultActionsUI
  }

  private convertActionToUI = (
    { displayNameKey, ...rest }: GlobalSearchAction,
    extension: GlobalSearchShellExtension,
  ) => ({
    ...rest,
    displayName: extension.getString(displayNameKey),
  })

  public getResultDefaultAction = (result: GlobalSearchResult, context: GlobalSearchContext) => {
    const extensions = this.getGlobalSearchExtensions()
    for (const extension of extensions) {
      const defaultAction = extension.getGlobalSearchResultDefaultAction(result, context)
      if (defaultAction) {
        return defaultAction
      }
    }
  }

  public convertToGlobalSearchResultPreview = (result: GlobalSearchResult) => {
    const extensions = this.getGlobalSearchExtensions()

    return extensions.reduce(
      (uiResult: GlobalSearchResultPreview, extension) => {
        const categoryId = extension.getGlobalSearchResultCategoryId(result)

        let defaultActionUI
        if (!uiResult.defaultAction) {
          const defaultAction = extension.getGlobalSearchResultDefaultAction(result, 'preview')
          if (defaultAction) {
            defaultActionUI = this.convertActionToUI(defaultAction, extension)
          }
        }

        if (categoryId) {
          const category = this.getCategoryById(categoryId)
          const title = this.getResultFieldValue(result, category?.titleField) ?? ''
          const preview = this.getResultFieldValue(result, category?.previewField) ?? ''
          const userKey = this.getResultFieldValue(result, category?.userKeyField) ?? ''
          const icon = this.getResultFieldValue(result, category?.iconField) ?? ''

          uiResult = {
            ...uiResult,
            title: uiResult.title || title,
            preview: uiResult.preview || preview,
            userKey: uiResult.userKey !== '' ? uiResult.userKey : userKey,
            icon: uiResult.icon !== '' ? uiResult.icon : icon,
            originalResult: uiResult.originalResult,
            defaultAction: uiResult.defaultAction ?? defaultActionUI,
          }
        }
        const extensionActions = extension.getGlobalSearchResultActions(result, 'preview')

        if (extensionActions) {
          const handledActions: GlobalSearchActionUI[] = []

          extensionActions.forEach(action => {
            if (this.canAddActionTo(action, uiResult.actions)) {
              handledActions.push(this.convertActionToUI(action, extension))
            }
          })
          uiResult = { ...uiResult, actions: [...uiResult.actions, ...handledActions] }
        }
        return uiResult
      },
      {
        title: '',
        preview: '',
        actions: [],
        userKey: '',
        icon: '',
        originalResult: result,
        defaultAction: undefined,
      } as GlobalSearchResultPreview,
    )
  }

  public convertToGlobalSearchResultPreviews = (
    results: readonly GlobalSearchResult[],
  ): readonly GlobalSearchResultPreview[] => results.map(result => this.convertToGlobalSearchResultPreview(result))

  public searchFor = async (
    searchString: string,
    categories?: readonly string[],
  ): Promise<readonly GlobalSearchResult[]> => {
    if (this.getCategories().length) {
      console.log('Search for ', searchString, categories)
      return await this.queryHardCodedResults(searchString)
    }
    return await Promise.resolve([])
  }

  private queryHardCodedResults = async (searchString: string) => {
    const data = [
      {
        userKey: 'result1',
        firstName: 'Kermit',
        lastName: 'TheFrog',
        keywords: '456',
        phone: '123-456-7890',
        email: '',
      },
      {
        userKey: 'result2',
        firstName: 'Miss',
        lastName: 'Piggy',
        keywords: '456',
        phone: '123-456-7890',
        email: '',
      },
      {
        userKey: 'result3',
        firstName: 'Fozzie',
        lastName: 'Bear',
        keywords: '456',
        phone: '123-456-7890',
        email: 'really456incrediblyextremelylongemailaddress@superlongemailaddressdomainnamewebsite.com',
      },
      {
        userKey: '3838948547100956682',
        firstName: 'Elisa',
        lastName: 'Matteo',
        keywords: '@shell',
        phone: '555-Elisa',
        email: 'elisa@goto.com',
      },
      {
        userKey: '4547668473900510970',
        firstName: 'Vincent',
        lastName: 'Audy',
        keywords: '@shell',
        phone: '555-Vincent',
        email: 'vincent@goto.com',
      },
      {
        userKey: '158619268522209074',
        firstName: 'Elise',
        lastName: 'Pelchat',
        keywords: '@shell',
        phone: '555-Elise',
        email: 'elise@goto.com',
      },
      {
        userKey: '602681216181426860',
        firstName: 'Marie',
        lastName: 'Foxall',
        keywords: '@shell',
        phone: '555-Marie',
        email: 'marie@goto.com',
      },
      {
        userKey: '6146506820683186259',
        firstName: 'Brian',
        lastName: 'Motherway',
        keywords: '@shell',
        phone: '555-Brian',
        email: 'brian@goto.com',
      },
      {
        userKey: '6300506601969632429',
        firstName: 'Daniel',
        lastName: 'Bart',
        keywords: '@shell',
        phone: '555-Daniel',
        email: 'daniel@goto.com',
      },
      {
        userKey: '7205586075161961226',
        firstName: 'Jose',
        lastName: 'Gomez',
        keywords: '@shell',
        phone: '555-Jose',
        email: 'jose@goto.com',
      },
      {
        userKey: '7205586075161961226',
        firstName: 'New',
        lastName: 'Entry',
        keywords: '@shell',
        phone: '555-Jose',
        email: 'jose@goto.com',
      },
      {
        userKey: '7205586075161961226',
        firstName: 'New',
        lastName: 'Test',
        keywords: '@shell',
        phone: '555-Jose',
        email: 'jose@goto.com',
      },
    ] as GlobalSearchResult[]
    const filteredResults = data.filter(result =>
      Object.keys(result).some(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        key => ((result as any)[key] as string).toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) >= 0,
      ),
    )
    return await Promise.resolve(filteredResults)
  }

  public getResultFieldValue = (result: GlobalSearchResult, field?: GlobalSearchValueField | undefined) => {
    if (field) {
      if (typeof field === 'string') {
        return result[field] as string
      }
      return field(result)
    }
  }

  private getCategoriesMap = () => {
    if (!this.categoriesMapInstance) {
      const result = new Map<string, GlobalSearchCategoryUI>()
      this.getGlobalSearchExtensions().forEach(extension => {
        extension.getGlobalSearchCategories().forEach(category => {
          const columnsUI = category.columns.map(column => this.convertColumnToUI(column, extension))
          const categoryUI = { ...category, columns: columnsUI }
          result.set(category.id, categoryUI)
        })
      })
      this.categoriesMapInstance = result
    }
    return this.categoriesMapInstance
  }

  private convertColumnToUI = (
    { displayNameKey, ...rest }: GlobalSearchCategoryColumn,
    extension: GlobalSearchShellExtension,
  ) => ({
    ...rest,
    displayName: extension.getString(displayNameKey),
  })

  private canAddActionTo(
    action: GlobalSearchAction,
    actionList: GlobalSearchActionUI[] | readonly GlobalSearchActionUI[],
  ): boolean {
    return isCommandHandled(action.command) && !actionList.find(existingAction => existingAction.id === action.id)
  }
  public getActionsForSearchString = (
    searchString: string,
    context: GlobalSearchContext,
  ): readonly GlobalSearchActionUI[] | undefined => {
    const extensions = this.getGlobalSearchExtensions()
    const result: GlobalSearchActionUI[] = extensions.reduce((actions: GlobalSearchActionUI[], extension) => {
      const extensionActions = extension.getActionsForSearchString(searchString, context)
      if (extensionActions) {
        extensionActions.forEach(action => {
          if (this.canAddActionTo(action, actions)) {
            actions.push(this.convertActionToUI(action, extension))
          }
        })
      }
      return actions
    }, [] as GlobalSearchActionUI[])

    return result
  }
}

const globalSearchManagerInstance = new GlobalSearchManagerImpl()

export const getGlobalSearchManager = (): GlobalSearchManager => globalSearchManagerInstance
